import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { formatTokenAmount } from './lib/amount';
import { type BlackBtcConfig, getLocalConfig, loadRuntimeConfig } from './lib/config';
import {
  type PreparedBlackBtcPayment,
  type TokenSnapshot,
  loadTokenSnapshot,
  prepareBlackBtcPayment,
  signAndSendBlackBtcPayment,
} from './lib/solana';
import {
  createLocalPaymentReceipt,
  explorerTransactionUrl,
  loadReceipts,
  storeReceipt,
  type LocalPaymentReceipt,
} from './lib/receipt';
import { readThirdwebBalance, type ThirdwebBalanceResult } from './lib/thirdweb';
import { createBlackBtcClient } from './lib/wallet';

type ViewName = 'send' | 'receive' | 'activity';

const PUMP_FUN_TOKEN_IMAGE = '/black-btc-logo.png';
const PUMP_FUN_TOKEN_PAGE = 'https://pump.fun/callouts/GgazaEccWY7t5BegwmJyUbWPCNQNv1HWmdag6Vp2pump/516e483d-8581-4231-8d59-35290b0d2e2f';

function shortAddress(value: string): string {
  return value.length > 14 ? `${value.slice(0, 7)}…${value.slice(-6)}` : value;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'The request could not be completed.';
}

function App() {
  const [config, setConfig] = useState<BlackBtcConfig>(getLocalConfig());
  const client = useMemo(
    () => createBlackBtcClient(config.rpcUrl, config.cluster),
    [config.cluster, config.rpcUrl],
  );
  const walletState = useSyncExternalStore(
    client.wallet.subscribe,
    client.wallet.getState,
    client.wallet.getState,
  );
  const walletAddress = walletState.connected?.account.address ?? '';
  const [snapshot, setSnapshot] = useState<TokenSnapshot | null>(null);
  const [thirdwebBalance, setThirdwebBalance] = useState<ThirdwebBalanceResult>({ available: false });
  const [activeView, setActiveView] = useState<ViewName>('send');
  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [requestAmount, setRequestAmount] = useState('');
  const [payment, setPayment] = useState<PreparedBlackBtcPayment | null>(null);
  const [receipts, setReceipts] = useState<LocalPaymentReceipt[]>(() => loadReceipts());
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const configured = Boolean(config.mint);
  const solBalance = snapshot ? (Number(snapshot.solLamports) / 1_000_000_000).toLocaleString(undefined, {
    maximumFractionDigits: 5,
  }) : '—';

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const requestedRecipient = query.get('recipient');
    const requestedAmount = query.get('amount');
    const requestedMint = query.get('mint');

    void loadRuntimeConfig().then((next) => {
      setConfig(next);
      if (requestedMint && next.mint && requestedMint !== next.mint) {
        setError('Payment request rejected because its token mint does not match the configured Black BTC mint.');
        return;
      }
      if (requestedRecipient) {
        setRecipient(requestedRecipient);
        setActiveView('send');
      }
      if (requestedAmount) setAmount(requestedAmount);
    });
  }, []);

  const refresh = useCallback(async (
    activeConfig = config,
    address = walletAddress,
  ) => {
    if (!address || !activeConfig.mint) {
      setSnapshot(null);
      setThirdwebBalance({ available: false });
      return;
    }
    setIsRefreshing(true);
    setError('');
    try {
      const nextSnapshot = await loadTokenSnapshot(activeConfig.rpcUrl, address, activeConfig.mint);
      setSnapshot(nextSnapshot);
      setThirdwebBalance(await readThirdwebBalance(activeConfig, address));
    } catch (refreshError) {
      setSnapshot(null);
      setError(errorMessage(refreshError));
    } finally {
      setIsRefreshing(false);
    }
  }, [config, walletAddress]);

  useEffect(() => {
    if (walletAddress && configured) void refresh();
  }, [configured, refresh, walletAddress]);

  useEffect(() => {
    setPayment(null);
    if (!walletAddress) {
      setSnapshot(null);
      setThirdwebBalance({ available: false });
    }
  }, [config.cluster, config.mint, walletAddress]);

  const thirdwebStatus = useMemo(() => {
    if (!config.thirdwebReady) return 'Optional balance check not configured';
    if (!thirdwebBalance.available) return thirdwebBalance.message ?? 'Thirdweb check pending';
    if (!snapshot) return 'Thirdweb available';
    return thirdwebBalance.value === snapshot.balanceBaseUnits.toString()
      ? 'Thirdweb balance matches Solana RPC'
      : 'Thirdweb and RPC balances differ—refresh before sending';
  }, [config.thirdwebReady, snapshot, thirdwebBalance]);

  async function connectWallet() {
    setIsConnecting(true);
    setError('');
    try {
      await client.wallet.whenReady();
      const wallets = client.wallet.getState().wallets;
      const selected = wallets.find((wallet) => /phantom/i.test(wallet.name))
        ?? wallets.find((wallet) => /solflare/i.test(wallet.name))
        ?? wallets[0];
      if (!selected) throw new Error('Install a Wallet Standard Solana wallet such as Phantom or Solflare.');
      await client.wallet.connect(selected);
      setNotice(`${selected.name} connected. No private key or seed phrase was shared with this app.`);
    } catch (connectError) {
      setError(errorMessage(connectError));
    } finally {
      setIsConnecting(false);
    }
  }

  async function disconnectWallet() {
    try {
      await client.wallet.disconnect();
    } finally {
      setSnapshot(null);
      setPayment(null);
    }
  }

  async function preparePayment() {
    const signer = client.wallet.getState().connected?.signer;
    if (!signer || !walletAddress) {
      setError('Connect a Solana wallet first.');
      return;
    }
    if (!configured) {
      setError('Black BTC payments are locked until the exact Solana mint is configured.');
      return;
    }
    setIsPreparing(true);
    setError('');
    setNotice('');
    setPayment(null);
    try {
      const prepared = await prepareBlackBtcPayment({
        rpcUrl: config.rpcUrl,
        signer,
        recipient,
        mintAddress: config.mint,
        amount,
      });
      setPayment(prepared);
      setNotice('Review the recipient, amount, mint, and SOL network fee before opening your wallet.');
    } catch (prepareError) {
      setError(errorMessage(prepareError));
    } finally {
      setIsPreparing(false);
    }
  }

  async function sendPayment() {
    if (!payment || !walletAddress) return;
    setIsSending(true);
    setError('');
    setNotice('Confirm the exact transfer in your Solana wallet.');
    try {
      const signature = await signAndSendBlackBtcPayment({ client, payment });
      const receipt = await createLocalPaymentReceipt({
        signature,
        mint: config.mint,
        sender: walletAddress,
        recipient: payment.recipient,
        amountBaseUnits: payment.baseUnits.toString(),
        amountDisplay: payment.amount,
        cluster: config.cluster,
        confirmedAt: new Date().toISOString(),
      });
      storeReceipt(receipt);
      setReceipts(loadReceipts());
      setPayment(null);
      setRecipient('');
      setAmount('');
      setNotice(`Payment confirmed: ${shortAddress(signature)}`);
      await refresh();
    } catch (sendError) {
      setError(errorMessage(sendError));
      setNotice('');
    } finally {
      setIsSending(false);
    }
  }

  async function copyText(value: string, successMessage: string) {
    try {
      await navigator.clipboard.writeText(value);
      setNotice(successMessage);
      setError('');
    } catch {
      setError('Clipboard access was denied. Copy the value manually.');
    }
  }

  async function copyPaymentRequest() {
    if (!walletAddress) {
      setError('Connect a wallet before creating a payment request.');
      return;
    }
    const url = new URL(window.location.href);
    url.search = '';
    url.searchParams.set('recipient', walletAddress);
    if (requestAmount.trim()) url.searchParams.set('amount', requestAmount.trim());
    if (config.mint) url.searchParams.set('mint', config.mint);
    await copyText(url.toString(), 'Payment request copied. Verify the mint before sharing it.');
  }

  function downloadReceipt(receipt: LocalPaymentReceipt) {
    const blob = new Blob([JSON.stringify(receipt, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `black-btc-receipt-${receipt.signature.slice(0, 10)}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <a className="brand" href="/" aria-label="Black BTC wallet home">
          <img className="coin-mark" src={PUMP_FUN_TOKEN_IMAGE} alt="Black Bitcoin token artwork" />
          <span>
            <strong>Black BTC</strong>
            <small>P2P Wallet</small>
          </span>
        </a>
        {walletAddress ? (
          <button className="wallet-pill" onClick={disconnectWallet} title="Disconnect wallet">
            <span className="status-dot" /> {shortAddress(walletAddress)}
          </button>
        ) : (
          <button className="primary compact" onClick={connectWallet} disabled={isConnecting}>
            {isConnecting ? 'Connecting…' : 'Connect wallet'}
          </button>
        )}
      </header>

      <section className="hero">
        <div>
          <p className="eyebrow">SOLANA PAYMENTS · ATHERA PROOF-READY</p>
          <h1>Send Black BTC like money.</h1>
          <p className="hero-copy">Direct wallet-to-wallet payments. You review every transfer, your Solana wallet signs it, and this app never receives your private key.</p>
        </div>
        <div className="hero-token">
          <img src={PUMP_FUN_TOKEN_IMAGE} alt="Black Bitcoin token artwork from Pump.fun" />
          <div className={`network-badge ${configured ? 'ready' : 'locked'}`}>
            <span>{configured ? '●' : '○'}</span>
            {configured ? `${config.cluster} configured` : 'Mint configuration required'}
          </div>
          <a className="token-source" href={PUMP_FUN_TOKEN_PAGE} target="_blank" rel="noreferrer">View token on Pump.fun ↗</a>
        </div>
      </section>

      {!configured && (
        <section className="setup-warning" role="status">
          <strong>Payments are safely locked.</strong>
          <span>Add the exact Pump.fun mint as <code>BLACK_BTC_SOLANA_MINT</code> in the backend or <code>VITE_BLACK_BTC_MINT</code> for local development.</span>
        </section>
      )}

      {(notice || error) && (
        <section className={error ? 'flash error' : 'flash success'} role="status">
          {error || notice}
        </section>
      )}

      <section className="balance-card">
        <div className="balance-heading">
          <img className="token-orb" src={PUMP_FUN_TOKEN_IMAGE} alt="Black Bitcoin token artwork" />
          <div>
            <span>{config.name}</span>
            <small>{config.symbol} · Solana</small>
          </div>
          <button className="icon-button" onClick={() => void refresh()} disabled={!walletAddress || !configured || isRefreshing} aria-label="Refresh balances">↻</button>
        </div>
        <div className="balance-value">{snapshot?.balanceDisplay ?? '0'} <span>{config.symbol}</span></div>
        <div className="balance-meta">
          <span>{solBalance} SOL available for network fees</span>
          <span>{thirdwebStatus}</span>
        </div>
      </section>

      <nav className="view-tabs" aria-label="Wallet actions">
        {(['send', 'receive', 'activity'] as const).map((view) => (
          <button key={view} className={activeView === view ? 'active' : ''} onClick={() => setActiveView(view)}>
            {view[0].toUpperCase() + view.slice(1)}
          </button>
        ))}
      </nav>

      {activeView === 'send' && (
        <section className="panel">
          <div className="section-title">
            <div><small>NEW PAYMENT</small><h2>Send {config.symbol}</h2></div>
            <span className="step">1 review · 1 signature</span>
          </div>

          <label>
            Recipient Solana address
            <input value={recipient} onChange={(event) => { setRecipient(event.target.value); setPayment(null); }} placeholder="Enter or paste a Solana address" autoComplete="off" spellCheck={false} />
          </label>
          <label>
            Amount
            <div className="amount-input">
              <input value={amount} onChange={(event) => { setAmount(event.target.value); setPayment(null); }} inputMode="decimal" placeholder="0.00" autoComplete="off" />
              <span>{config.symbol}</span>
            </div>
          </label>
          {snapshot && (
            <button className="text-button" onClick={() => { setAmount(snapshot.balanceDisplay); setPayment(null); }}>Use available balance</button>
          )}

          {!walletAddress ? (
            <button className="primary full" onClick={connectWallet}>Connect wallet to send</button>
          ) : (
            <button className="primary full" onClick={preparePayment} disabled={!configured || !recipient.trim() || !amount.trim() || isPreparing}>
              {isPreparing ? 'Preparing review…' : 'Review payment'}
            </button>
          )}

          {payment && (
            <div className="review-card">
              <div className="review-header"><strong>Payment review</strong><span>Not sent</span></div>
              <dl>
                <div><dt>You send</dt><dd>{formatTokenAmount(payment.baseUnits, payment.decimals, payment.decimals)} {config.symbol}</dd></div>
                <div><dt>Recipient</dt><dd title={payment.recipient}>{shortAddress(payment.recipient)}</dd></div>
                <div><dt>Token mint</dt><dd title={config.mint}>{shortAddress(config.mint)}</dd></div>
                <div><dt>Network</dt><dd>Solana {config.cluster}</dd></div>
                <div><dt>Network fee</dt><dd>Calculated by your wallet</dd></div>
              </dl>
              <p>Associated token-account rent may be added if the recipient has never held this token.</p>
              <button className="primary full" onClick={sendPayment} disabled={isSending}>
                {isSending ? 'Waiting for confirmation…' : 'Confirm in Solana wallet'}
              </button>
            </div>
          )}
        </section>
      )}

      {activeView === 'receive' && (
        <section className="panel">
          <div className="section-title"><div><small>GET PAID</small><h2>Receive {config.symbol}</h2></div></div>
          {walletAddress ? (
            <>
              <div className="address-card">
                <span>Your Solana address</span>
                <strong>{walletAddress}</strong>
                <button className="secondary full" onClick={() => copyText(walletAddress, 'Wallet address copied.')}>Copy address</button>
              </div>
              <label>
                Optional requested amount
                <div className="amount-input"><input value={requestAmount} onChange={(event) => setRequestAmount(event.target.value)} inputMode="decimal" placeholder="0.00" /><span>{config.symbol}</span></div>
              </label>
              <button className="primary full" onClick={copyPaymentRequest}>Copy payment request link</button>
              <p className="privacy-note">The request contains only your public Solana address, amount, and configured mint. It never contains a key or seed phrase.</p>
            </>
          ) : (
            <button className="primary full" onClick={connectWallet}>Connect wallet to receive</button>
          )}
        </section>
      )}

      {activeView === 'activity' && (
        <section className="panel">
          <div className="section-title"><div><small>THIS DEVICE</small><h2>Payment receipts</h2></div><span className="step">Local only</span></div>
          {receipts.length === 0 ? (
            <div className="empty-state"><span>↗</span><strong>No payments yet</strong><p>Confirmed payments will appear here as hash-only receipt records stored on this device.</p></div>
          ) : receipts.map((receipt) => (
            <article className="receipt-row" key={receipt.signature}>
              <span className="receipt-icon">✓</span>
              <div>
                <strong>−{receipt.amountDisplay} {config.symbol}</strong>
                <small>To {shortAddress(receipt.recipient)} · {new Date(receipt.confirmedAt).toLocaleString()}</small>
                <a href={explorerTransactionUrl(receipt.signature, receipt.cluster)} target="_blank" rel="noreferrer">View confirmed transaction</a>
              </div>
              <button className="icon-button" onClick={() => downloadReceipt(receipt)} title="Download receipt">↓</button>
            </article>
          ))}
        </section>
      )}

      <section className="proof-card">
        <div className="proof-icon">ZK</div>
        <div>
          <strong>Athera private proof layer</strong>
          <p>The wallet is ready to attach an Athera holder proof and anchor only a payment-receipt hash. This release does not claim private Solana transfers or a live Athera relay.</p>
        </div>
        <span>Integration pending</span>
      </section>

      <footer>
        <span>Non-custodial · Exact-mint locked · No hidden fees</span>
        <span>Never enter a seed phrase or private key into this app.</span>
      </footer>
    </main>
  );
}

export default App;
